<template>
  <ActionsWidget v-show="props.hasUnsavedChanges">
    <div class="flex items-center w-full">
      <div>You've got some unsaved changes.</div>
      <div class="flex-1"></div>
      <div class="flex space-x-2">
        <SelectionWidgetMenuItem
          text="Reset"
          icon="arrow-uturn-left"
          hotkey="r"
          @click="props.resetChanges"
        />
        <SelectionWidgetMenuItem
          text="Save changes"
          icon="check"
          :pending="props.saving"
          :disabled="(invalidations || []).length > 0"
          allow-clicking-despite-disablement
          hotkey="s"
          variant="primary"
          @click="handleSave"
        >
          <template v-if="props.invalidations?.length" #content>
            <div class="space-y-2">
              <div
                v-for="invalidation in props.invalidations"
                :key="invalidation.selector"
              >
                {{ invalidation.message }}
              </div>
            </div>
          </template>
        </SelectionWidgetMenuItem>
      </div>
    </div>
  </ActionsWidget>
</template>

<script lang="ts" setup>
import { nextTick, ref } from "vue";

import ActionsWidget from "@/components/Layout/ListView/ActionsWidget.vue";
import SelectionWidgetMenuItem from "@/components/Layout/ListView/SelectionWidgetMenuItem.vue";

export type Invalidation = {
  selector?: string;
  message: string;
};

export interface Props {
  saving?: boolean;
  resetChanges?: () => void;
  hasUnsavedChanges?: boolean;
  save?: (() => void) | (() => Promise<void>);
  invalidations?: Invalidation[];
}

const props = defineProps<Props>();

const shakeCount = ref(0);

let pendingSave: Promise<void> | null = null;

const handleSave = () => {
  // Save key got pressed but there are no unsaved changes.
  if (!props.hasUnsavedChanges) return;
  if (pendingSave) {
    return;
  }

  const invalidations = props.invalidations || [];
  if (invalidations.length > 0) {
    const selector = invalidations[0].selector;
    if (selector) {
      const element = document.querySelector(selector);
      if (element) {
        // If the element is not in view, scroll until it is.
        if (
          !element.getBoundingClientRect().top ||
          element.getBoundingClientRect().top < 0 ||
          element.getBoundingClientRect().bottom > window.innerHeight
        ) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        shakeCount.value = Math.min(shakeCount.value + 1, 3);
        element.classList.remove(
          ...Array.from(
            { length: shakeCount.value },
            (_, i) => `shaking-${i + 1}`
          )
        );
        nextTick(() => {
          element.classList.add(`shaking-${shakeCount.value}`);
        });
      }
    }
  } else {
    const result = props.save?.();
    if (result instanceof Promise) {
      pendingSave = result.finally(() => {
        pendingSave = null;
      });
    }
    shakeCount.value = 0;
  }
};
</script>

<style>
@keyframes tilt-n-move-shaking-1 {
  0% {
    transform: translateX(0);
  }
  35% {
    transform: rotate(1deg);
  }
  55% {
    transform: rotate(-1deg);
  }
  65% {
    transform: rotate(1deg);
  }
  75% {
    transform: rotate(-1deg);
  }
  100% {
    transform: translateY(0) rotate(0);
  }
}

@keyframes tilt-n-move-shaking-2 {
  0% {
    transform: translateX(0);
  }
  35% {
    transform: rotate(2deg);
  }
  55% {
    transform: rotate(-2deg);
  }
  65% {
    transform: rotate(2deg);
  }
  75% {
    transform: rotate(-2deg);
  }
  100% {
    transform: translateY(0) rotate(0);
  }
}

@keyframes tilt-n-move-shaking-3 {
  0% {
    transform: translateX(0);
  }
  35% {
    transform: rotate(3deg);
  }
  55% {
    transform: rotate(-3deg);
  }
  65% {
    transform: rotate(3deg);
  }
  75% {
    transform: rotate(-3deg);
  }
  100% {
    transform: translateY(0) rotate(0);
  }
}

.shaking-1 {
  animation: tilt-n-move-shaking-1 0.5s;
}
.shaking-2 {
  animation: tilt-n-move-shaking-2 0.5s;
}
.shaking-3 {
  animation: tilt-n-move-shaking-3 0.5s;
}
</style>
